/* eslint-disable @nx/enforce-module-boundaries */
import { Route } from '@angular/router';

import { AuthGuard } from './guard/auth.guard';
import { githubRedirectGuard } from './guard/github-redirect.guard';
import { OrgPrefixRedirectGuard } from './guard/org-prefix.guard';
import { SuperUserGuard } from './guard/superUser.guard';
import { OrganizationRouteMatcher } from './matchers/org-route.matcher';

export default [
  {
    path: 'auth',
    loadChildren: () => import('@envisage/auth').then(auth => auth.AUTH_ROUTES),
    title: 'Authentication'
  },
  {
    path: 'onboarding/org/:orgId/user/:userId/invitation/:invitationId/:action',
    loadComponent: () => import('@envisage/onboarding').then(m => m.InviteOrganizationComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'account-dashboard',
    loadChildren: () => import('@envisage/onboarding').then(dashboard => dashboard.ACCOUNT_DASHBOARD_ROUTES),
    title: 'Account Dashboard',
    canActivate: [AuthGuard]
  },
  {
    path: 'onboarding',
    loadChildren: () => import('@envisage/onboarding').then(onboarding => onboarding.ONBOARDING_ROUTES),
    title: 'Onboarding',
    canActivate: [AuthGuard]
  },
  {
    path: 'org/:orgId',
    loadComponent: () => import('./pages/main-layout/main-layout.component').then(m => m.MainLayoutComponent),
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        redirectTo: 'schedule',
        pathMatch: 'full'
      },
      {
        path: 'project',
        loadChildren: () => import('@envisage/project').then(projects => projects.PROJECT_ROUTES),
        title: 'Project'
      },
      {
        path: 'schedule',
        loadChildren: () => import('@envisage/calender').then(schedule => schedule.CALENDER_ROUTES),
        title: 'Schedule'
      },
      {
        path: '',
        loadComponent: () =>
          import('@envisage/organization').then(organization => organization.OrganizationHomeComponent),
        children: [
          {
            path: 'components',
            loadChildren: () => import('@envisage/component').then(component => component.COMPONENT_ROUTES),
            title: 'Components'
          },
          {
            path: 'tasks',
            loadChildren: () => import('@envisage/task').then(task => task.TASK_ROUTES),
            title: 'Tasks'
          },
          {
            path: 'tickets',
            loadChildren: () => import('@envisage/ticket').then(ticket => ticket.TICKET_ROUTES),
            title: 'Tickets'
          },
          {
            path: 'issues',
            loadChildren: () => import('@envisage/issue').then(issues => issues.ISSUE_ROUTES),
            title: 'Issues',
            canActivate: [AuthGuard]
          },
          {
            path: 'publishes',
            loadChildren: () => import('@envisage/publish-reviews').then(publish => publish.PUBLISH_ROUTES),
            title: 'Publishes',
            canActivate: [AuthGuard]
          },
          {
            path: 'publish-reviews',
            loadChildren: () => import('@envisage/publish-reviews').then(publish => publish.REVIEWS_ROUTES),
            title: 'Publish Reviews',
            canActivate: [AuthGuard]
          },
          {
            path: 'admin',
            loadChildren: () =>
              import('@envisage/configuration').then(configuration => configuration.ADMIN_CONFIGURATION_ROUTES),
            title: 'Admin',
            canActivate: [SuperUserGuard]
          }
        ]
      },
      {
        path: '',
        loadChildren: () => import('@envisage/organization').then(organization => organization.ORGANIZATION_ROUTES),
        title: 'Organization',
        canActivate: [SuperUserGuard]
      },
      {
        path: 'groups',
        loadChildren: () => import('@envisage/milestone').then(milestone => milestone.MILESTONE_ROUTES),
        title: 'Milestones'
      },
      {
        path: 'reports',
        loadChildren: () => import('@envisage/reports').then(report => report.REPORTS_ROUTES),
        title: 'Reports'
      },
      {
        path: 'support',
        loadChildren: () => import('@envisage/support').then(support => support.SUPPORT_ROUTES),
        title: 'Support'
      },
      {
        path: 'user',
        loadChildren: () => import('@envisage/user').then(user => user.USER_ROUTES),
        title: 'User'
      },
      {
        path: 'dashboard',
        loadChildren: () => import('@envisage/dashboard').then(dashboard => dashboard.DASHBOARD_ROUTES),
        title: 'Dashboard'
      }
    ]
  },
  {
    path: 'github/callback',
    canActivate: [AuthGuard, githubRedirectGuard],
    children: []
  },
  {
    path: 'github/setup',
    canActivate: [AuthGuard, githubRedirectGuard],
    children: []
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('./layout/privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent),
    title: 'Privacy Policy',
    pathMatch: 'full'
  },
  {
    path: 'tos',
    loadComponent: () =>
      import('./layout/terms-of-service/terms-of-service.component').then(m => m.TermsOfServiceComponent),
    title: 'Terms of Service',
    pathMatch: 'full'
  },
  {
    path: 'license',
    loadComponent: () =>
      import('./layout/software-license-agreement/software-license-agreement.component').then(
        m => m.SoftwareLicenseAgreementComponent
      ),
    title: 'Software License Agreement',
    pathMatch: 'full'
  },
  {
    path: 'not-found',
    loadComponent: () => import('./layout/page-not-found/page-not-found.component').then(m => m.PageNotFoundComponent),
    pathMatch: 'full'
  },
  {
    path: 'access-denied',
    loadComponent: () => import('./layout/access-denied/access-denied.component').then(m => m.AccessDeniedComponent),
    pathMatch: 'full'
  },
  {
    path: 'maintenance',
    loadComponent: () => import('./layout/maintenance/maintenance.component').then(m => m.MaintenanceComponent),
    pathMatch: 'full'
  },
  // Envisage Connect Invitation Route
  {
    path: 'clients',
    children: [
      {
        path: '',
        redirectTo: 'setup',
        pathMatch: 'full'
      },
      {
        path: 'setup',
        loadComponent: () =>
          import('./layout/envisage-connect-entity-invitation/envisage-connect-entity-invitation.component').then(
            m => m.EnvisageConnectEntityInvitationComponent
          )
      }
    ]
  },
  // Envisage Connect Invitation Route
  {
    path: 'vendors',
    children: [
      {
        path: '',
        redirectTo: 'setup',
        pathMatch: 'full'
      },
      {
        path: 'setup',
        loadComponent: () =>
          import('./layout/envisage-connect-entity-invitation/envisage-connect-entity-invitation.component').then(
            m => m.EnvisageConnectEntityInvitationComponent
          )
      }
    ]
  },
  {
    matcher: OrganizationRouteMatcher,
    canMatch: [OrgPrefixRedirectGuard],
    children: []
  },
  {
    path: '',
    redirectTo: 'account-dashboard',
    pathMatch: 'full'
  },
  { path: '**', redirectTo: 'not-found', pathMatch: 'full' }
] satisfies Route[];
